"use client";
import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import { useColorScheme } from "@mui/joy/styles";
import { Box, Card, Typography } from "@mui/joy";
import { useProject } from "../../../../hooks/query";

const MyChart = () => {
    const { mode } = useColorScheme(); // mode: 'light' or 'dark'
    const { useList } = useProject();
    const listProjectSvc = useList();
    const [chartData, setChartData] = useState<{ value: number; name: string }[]>([]);

    useEffect(() => {
        listProjectSvc.mutateAsync().catch(console.error);
    }, []);

    useEffect(() => {
        if (!listProjectSvc.data) return;

        const projects = listProjectSvc.data;
        const counts: Record<string, number> = {
            "NOT-STARTED": 0,
            "IN-PROGRESS": 0,
            COMPLETED: 0,
            HOLD: 0,
            CANCELLED: 0,
        };

        projects.forEach((p: any) => {
            if (counts[p.Status] !== undefined) counts[p.Status]++;
            else counts[p.Status] = 1;
        });

        const dataMap: Record<string, string> = {
            "NOT-STARTED": "Not Started",
            "IN-PROGRESS": "In Progress",
            COMPLETED: "Completed",
            HOLD: "On Hold",
            CANCELLED: "Cancelled",
        };

        setChartData(
            Object.keys(counts)
                .map((key) => ({
                    value: counts[key],
                    name: dataMap[key] || key,
                }))
                .filter((d) => d.value > 0),
        );
    }, [listProjectSvc.data]);

    const option = {
        backgroundColor: "transparent",
        tooltip: {
            trigger: "item",
            formatter: "{b} : {c} ({d}%)",
            backgroundColor: mode === "dark" ? "#1f2937" : "#ffffff",
            borderColor: mode === "dark" ? "#374151" : "#e5e7eb",
            textStyle: {
                color: mode === "dark" ? "#f9fafb" : "#111827",
                fontFamily: "Inter, sans-serif",
            },
        },

        legend: {
            bottom: "5%",
            left: "center",
            textStyle: {
                color: mode === "dark" ? "#9ca3af" : "#6b7280",
                fontFamily: "Inter, sans-serif",
            },
            itemGap: 15,
            itemWidth: 12,
            itemHeight: 12,
            icon: "circle",
        },
        series: [
            {
                name: "Project Status",
                type: "pie",
                radius: ["40%", "75%"],
                center: ["50%", "45%"],
                itemStyle: {
                    borderRadius: 8,
                    borderColor: mode === "dark" ? "#1E1E1E" : "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 14,
                        fontWeight: "bold",
                    },
                },
                color: [
                    "#fac858", // Not started (yellow)
                    "#5470c6", // In progress (blue)
                    "#3e8e41", // Completed (green)
                    "#ee6666", // Hold (red)
                    "#91cc75", // Cancelled (light green)
                ],
                data: chartData.length > 0 ? chartData : [{ value: 0, name: "No Data" }],
            },
        ],
    };

    return (
        <Card
            variant="plain"
            sx={{
                display: "flex",
                flexDirection: "column",
                p: 2.5,
                height: "100%",
                boxShadow: "sm",
                borderRadius: "xl",
                bgcolor: "background.surface",
                border: "none",
            }}
        >
            <Typography level="title-lg" sx={{ fontWeight: 700, mb: 1 }}>
                Project Status Summary
            </Typography>
            <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                <ReactECharts
                    option={option}
                    theme={mode} // Automatically applies 'light' or 'dark'
                    style={{ width: "100%", height: "100%", minHeight: 250 }}
                    notMerge={true}
                />
            </Box>
        </Card>
    );
};

export default MyChart;
